import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { API, EASE } from "@/lib/brand";
import { useCart } from "@/context/CartContext";
import { LogoMark } from "@/components/site/Logo";

const Shell = ({ children }) => (
  <main className="relative min-h-screen grid place-items-center px-5 py-32 overflow-hidden">
    <img src="/images/hero.jpg" alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-25" />
    <div className="absolute inset-0 bg-gradient-to-b from-[#07090D]/70 to-[#07090D]" />
    <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: EASE }} className="relative max-w-xl text-center">
      <LogoMark draw className="h-14 w-auto mx-auto mb-8" />
      {children}
      <Link to="/" data-testid="back-home-link" className="glow-btn mt-10 inline-flex">Back to MenTellHealth</Link>
    </motion.div>
  </main>
);

export const PaymentCancel = () => (
  <Shell>
    <XCircle className="h-8 w-8 mx-auto text-slate-300" />
    <h1 data-testid="payment-cancel-title" className="mt-4 font-display font-black uppercase text-4xl sm:text-5xl">Checkout cancelled</h1>
    <p className="mt-4 text-slate-300">No payment was taken. Your bag is saved for whenever you're ready.</p>
  </Shell>
);

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clear } = useCart();
  const [state, setState] = useState(sessionId ? "checking" : "error");

  useEffect(() => {
    if (!sessionId) return undefined;
    let tries = 0;
    let timer;
    let alive = true;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (!alive) return;
        if (data?.payment_status === "paid") {
          setState("paid");
          clear();
          return;
        }
        if (data?.status === "expired") {
          setState("error");
          return;
        }
      } catch {
        if (!alive) return;
      }
      tries += 1;
      if (tries >= 10) setState("pending");
      else timer = setTimeout(poll, 2000);
    };
    poll();
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [sessionId, clear]);

  return (
    <Shell>
      {state === "checking" && (
        <div data-testid="payment-checking"><Loader2 className="h-8 w-8 mx-auto animate-spin text-[var(--mth-blue)]" /><p className="mt-4 text-slate-300">Confirming your payment…</p></div>
      )}
      {state === "paid" && (
        <div data-testid="payment-success">
          <CheckCircle2 className="h-10 w-10 mx-auto text-[var(--mth-blue)]" />
          <h1 className="mt-4 font-display font-black uppercase text-4xl sm:text-6xl leading-[0.9]">Order confirmed.</h1>
          <p className="mt-5 text-slate-300">Thank you for wearing the conversation. A receipt is on its way to your inbox.</p>
          <p data-testid="order-reference" className="mt-4 font-mono text-xs uppercase tracking-widest text-slate-400">Ref: {sessionId?.slice(-10).toUpperCase()}</p>
          <p className="mt-8 font-display text-xl">Talk. Connect. <span className="text-[var(--mth-blue)]">Keep going.</span></p>
        </div>
      )}
      {state === "pending" && <p data-testid="payment-pending" className="text-slate-300">We're still confirming your payment. Check your email for a receipt shortly.</p>}
      {state === "error" && <p data-testid="payment-error" className="text-slate-300">We couldn't find that payment. If you were charged, your receipt email has the details.</p>}
    </Shell>
  );
}
